import { WrongCredentialsError } from '../../errors';
import CharCodes from '../EventSourcePolyfill/CharCodes';
import NetworkStatus from '../NetworkStatus';
import WebSocketRequest, { OnError, OnMessage, OnOpen } from './WebSocketRequest';

type PartialHttpClient = {
  getAuth: () => void;
  config: { webSocketUrl: string };
  getAuthForKeyAndSecret: (key: string, secret: string) => unknown;
  networkStatus: string;
  _setNetworkStatus: (status: unknown) => void;
};

class WebSocketClient {
  httpClient: PartialHttpClient;
  connected: Promise<unknown>;
  _resolveConnected?: (value?: Error) => void;
  hasAuthMessage: boolean; // = false
  hasConnected: boolean;
  hasError: boolean;
  hasFirstMessage: boolean;
  _auth: { key: string; secret: string };
  _onError?: OnError;
  _onMessage?: OnMessage;
  _onOpen?: OnOpen;
  _request?: WebSocketRequest;
  _features: string[];
  constructor({
    auth,
    httpClient,
    onError,
    onMessage,
    onOpen,
    path,
    features,
  }: {
    auth: { key: string; secret: string };
    httpClient: PartialHttpClient;
    path: string;
    onOpen?: OnOpen;
    onMessage?: OnMessage;
    onError?: OnError;
    features: string[];
  }) {
    this.httpClient = httpClient;
    this.connected = new Promise((resolve) => {
      this._resolveConnected = resolve;
    });
    this.hasAuthMessage = false;
    this.hasConnected = false;
    this.hasError = false;
    this.hasFirstMessage = false;

    this._auth = auth || this.httpClient.getAuth();
    this._onError = onError;
    this._onMessage = onMessage;
    this._onOpen = onOpen;
    this._features = features;

    const url = /wss?:/.test(path)
      ? path
      : this.httpClient.config.webSocketUrl.replace(/^https?:/, 'wss:') + path;

    this._request = new WebSocketRequest({
      onError: (event: Error) => this.onError(event),
      onMessage: (event: { data: string }, flags: unknown) => this.onMessage(event, flags),
      onOpen: () => this.onOpen(),
      url,
    });
  }

  onOpen() {
    this._request?.send(`v1_auth: ${JSON.stringify(this.getAuthInfo())}`);
  }

  getAuthInfo() {
    const { key, secret } = this._auth;

    return {
      scheme: 'Basic',
      credentials: this.httpClient.getAuthForKeyAndSecret(key, secret),
      features: this._features,
    };
  }

  onMessage(event: { data: string }, _flags: unknown) {
    const { data } = event;
    let command = '';
    let payload = '';
    let res;

    for (let pos = 0; pos < data.length; pos++) {
      if (data.charCodeAt(pos) !== CharCodes[':']) continue;

      command = data.substring(0, pos);
      pos++;

      while (data.charCodeAt(pos) === CharCodes[' '] && pos < data.length) {
        pos++;
      }
      payload = data.substring(pos);
      break;
    }

    if (!this.hasAuthMessage) {
      this.hasAuthMessage = true;

      if (!command) {
        return this.sendWrongCredsErr(
          `Could not establish WebSocket connection: could not parse auth payload: "${data}"`
        );
      } else if (command !== 'v1_auth') {
        return this.sendWrongCredsErr(
          `Could not establish WebSocket connection: expected auth command but got: "${command}"`
        );
      }

      try {
        res = JSON.parse(payload);
      } catch (error) {
        return this.sendWrongCredsErr(
          `Could not establish WebSocket connection: could not parse auth payload: "${payload}"`
        );
      }

      if (res.code === 200) {
        this.hasConnected = true;
        this._resolveConnected && this._resolveConnected();
        this.httpClient._setNetworkStatus(NetworkStatus.ONLINE);
        if (this._onOpen) this._onOpen();
        return;
      } else {
        return this.sendWrongCredsErr(
          `Could not establish WebSocket connection: auth failure code: ${res.code}, message: "${res.message}"`
        );
      }
    }

    if (command === 'data') {
      const isFirstMessage = !this.hasFirstMessage;
      this.hasFirstMessage = true;
      if (this._onMessage) this._onMessage({ data: payload }, { isFirstMessage });
    } else {
      console.log(`WebSocketClient: Unknown command: "${command}"`);
    }
  }

  sendWrongCredsErr(message: string) {
    this.onError(new WrongCredentialsError({}, message));
    this._request?.close();
  }

  onError(event: Error) {
    if (this.hasError) return;
    this.hasError = true;

    if (this.httpClient.networkStatus !== NetworkStatus.OFFLINE) {
      this.httpClient._setNetworkStatus(NetworkStatus.UNREACHABLE);
    }

    if (this._onError) this._onError(event);
    if (!this.hasConnected) this._resolveConnected && this._resolveConnected(event);
  }

  close(code: number, reason?: string) {
    return this._request?.close(code, reason);
  }

  send(message: string) {
    return this._request?.send(message);
  }
}

export default WebSocketClient;
